import { z } from 'zod';
import { SLUG_REGEX, slugSchema, visibleLength } from './slug';

// state_slug = immutable wire identifier (LLM tool args, ClickHouse path, placeholder lookup, prompt-tag attribute). state_name = freely editable display label.

// {} and null collapse to null at the contract boundary; undefined stays undefined so PATCH partial-update guards still work.
const initialValueCreateSchema = z
  .record(z.unknown())
  .nullish()
  .transform((v) => (v && Object.keys(v).length > 0 ? v : null));

const initialValueUpdateSchema = z
  .record(z.unknown())
  .nullish()
  .transform((v) => (v === undefined ? undefined : v && Object.keys(v).length > 0 ? v : null));

// Param schemas
export const stateIdParam = z.object({
  id: z.string().uuid(),
});

// Query schemas
export const listStatesQuery = z.object({
  hub_id: z.string().uuid(),
});

export const stateNameSchema = z
  .string()
  .min(1, 'state_name is required')
  .max(100, 'state_name must be 100 characters or fewer')
  .refine((v) => visibleLength(v) > 0, 'state_name must not be blank');

// Body schemas
export const createStateBody = z.object({
  hub_id: z.string().uuid(),
  state_slug: slugSchema,
  state_name: stateNameSchema,
  state_scope: z.enum(['conversation', 'user']),
  state_description: z.string().max(1000).nullish(),
  json_schema: z.record(z.unknown()).default({}),
  initial_value: initialValueCreateSchema,
  display_order: z.number().int().nonnegative().optional(),
  is_enabled: z.boolean().default(true),
  inject_in_last_message: z.boolean().default(true),
});

export const updateStateBody = z.object({
  // state_slug is immutable after first persist — not accepted here.
  state_name: stateNameSchema.optional(),
  state_scope: z.enum(['conversation', 'user']).optional(),
  state_description: z.string().max(1000).nullish(),
  json_schema: z.record(z.unknown()).optional(),
  initial_value: initialValueUpdateSchema,
  display_order: z.number().int().nonnegative().optional(),
  is_enabled: z.boolean().optional(),
  inject_in_last_message: z.boolean().optional(),
});

export const reorderStatesBody = z.object({
  states: z.array(z.object({
    state_id: z.string().uuid(),
    display_order: z.number().int().nonnegative(),
  })).min(1),
});

// Re-export the slug regex under a state-specific name for callers that want
// to validate ad-hoc (migrations, slugify helpers).
export const STATE_SLUG_REGEX = SLUG_REGEX;

// Inferred types
export type CreateStateInput = z.infer<typeof createStateBody>;
export type UpdateStateInput = z.infer<typeof updateStateBody>;
export type ReorderStatesInput = z.infer<typeof reorderStatesBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

export interface StateResponse {
  state_id: string;
  created_at: string;
  updated_at: string;
  hub_id: string;
  state_slug: string;
  state_name: string;
  state_scope: 'conversation' | 'user';
  state_description: string | null;
  json_schema: Record<string, unknown>;
  initial_value: Record<string, unknown> | null;
  display_order: number;
  is_enabled: boolean;
  inject_in_last_message: boolean;
}

export interface ListStatesResponse {
  data: StateResponse[];
}

export interface GetStateResponse {
  data: StateResponse;
}

export interface CreateStateResponse {
  data: StateResponse;
}

export interface UpdateStateResponse {
  data: StateResponse;
}
